/* Kenra Video List — in-place topic filtering + progressive ("infinite scroll")
 * reveal for the Video page grid.
 *
 * All video cards are server-rendered once (each <li> carries data-topics — the
 * article's downcased tags). Filtering and the reveal are DOM-only (no fetch):
 *   - Topic links (desktop dropdown + mobile panel) filter the grid in place and
 *     update a ?topic=<tag> deep link; topics are real /tagged/ anchors so no-JS
 *     users still get a server-filtered view.
 *   - Only the first `batch-size` matching items show; an IntersectionObserver
 *     sentinel reveals the next batch as the user scrolls.
 */
(function () {
  // A single window-level popstate listener (popstate doesn't bubble, so it
  // can't be scoped to a section). It dispatches to whatever video-list roots
  // are live at event time — querying the DOM here means detached roots from a
  // previous shopify:section:load are naturally skipped, so this never stacks.
  var popstateBound = false;
  function bindPopstateOnce() {
    if (popstateBound) return;
    popstateBound = true;
    window.addEventListener('popstate', function () {
      var topic = new URLSearchParams(window.location.search).get('topic') || 'all';
      document.querySelectorAll('[data-video-list]').forEach(function (r) {
        if (typeof r.kenraVideoApplyTopic === 'function') r.kenraVideoApplyTopic(topic);
      });
    });
  }

  function initVideoList(root) {
    if (root.dataset.videoListInit === 'true') return;
    root.dataset.videoListInit = 'true';

    var grid = root.querySelector('[data-video-grid]');
    if (!grid) return;

    var items = Array.prototype.slice.call(grid.querySelectorAll('.kenra-video-list__item'));
    var sentinel = root.querySelector('[data-video-sentinel]');
    var emptyMsg = root.querySelector('[data-video-empty]');
    var filterToggle = root.querySelector('[data-video-filter-toggle]');
    var filterMenu = root.querySelector('[data-video-filter-menu]');

    // Mobile filter lives in a sibling .kenra-scope (outside this root).
    var section = root.closest('.shopify-section') || document;

    // Both the desktop dropdown button and the mobile bottom FAB carry a
    // [data-video-filter-label] span; both must reflect the selected topic
    // (matching kenraprofessional.com, where the mobile button shows the topic
    // name). Query at section scope so the mobile FAB — which lives outside
    // `root`, in the sibling .kenra-scope — is included.
    var filterLabelEls = section.querySelectorAll('[data-video-filter-label]');
    var mobileScope = section.querySelector('[data-video-list-mobile]');
    var filterPanel = mobileScope && mobileScope.querySelector('[data-video-filter-panel]');
    var filterOpenBtn = mobileScope && mobileScope.querySelector('[data-video-filter-open]');

    var batchSize = parseInt(root.dataset.batchSize, 10) || 24;
    var defaultLabel = filterLabelEls.length ? filterLabelEls[0].textContent.trim() : 'Topics';
    var state = { topic: 'all', limit: batchSize };

    // Stable base path for history URLs — the "All topics" link points at the
    // blog root (e.g. /blogs/video). Building ?topic= against this (not the
    // current pathname) keeps history correct even on a /tagged/<tag> URL.
    var allLink = section.querySelector('[data-video-topic="all"]');
    var basePath = window.location.pathname;
    if (allLink) {
      try {
        basePath = new URL(allLink.getAttribute('href'), window.location.origin).pathname;
      } catch (_e) {
        /* keep current pathname */
      }
    }

    function topicLinks() {
      return section.querySelectorAll('[data-video-topic]');
    }

    function matches(item) {
      if (state.topic === 'all') return true;
      var topics = ' ' + (item.dataset.topics || '') + ' ';
      return topics.indexOf(' ' + state.topic + ' ') >= 0;
    }

    function apply() {
      var shown = 0;
      var matchCount = 0;
      items.forEach(function (item) {
        if (matches(item)) {
          matchCount++;
          if (shown < state.limit) {
            item.hidden = false;
            shown++;
          } else {
            item.hidden = true;
          }
        } else {
          item.hidden = true;
        }
      });
      if (emptyMsg) emptyMsg.hidden = matchCount !== 0;
      // Hide the sentinel once everything matching is shown (stops observing work).
      if (sentinel) sentinel.style.display = shown < matchCount ? '' : 'none';
    }

    function setActive() {
      topicLinks().forEach(function (link) {
        var active = link.getAttribute('data-video-topic') === state.topic;
        link.classList.toggle('is-active', active);
        if (active) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      var labelText = defaultLabel;
      if (state.topic !== 'all') {
        var current = section.querySelector('[data-video-topic="' + state.topic + '"]');
        labelText = current ? current.getAttribute('data-label') : defaultLabel;
      }
      filterLabelEls.forEach(function (el) {
        el.textContent = labelText;
      });
    }

    function setTopic(topic, pushHistory) {
      state.topic = topic || 'all';
      state.limit = batchSize;
      setActive();
      apply();
      if (pushHistory) {
        var url =
          state.topic === 'all'
            ? basePath
            : basePath + '?topic=' + encodeURIComponent(state.topic);
        window.history.pushState({ videoTopic: state.topic }, '', url);
        var top = root.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: top, behavior: 'auto' });
      }
    }

    /* ----- desktop dropdown ----- */
    function closeMenu() {
      if (!filterMenu) return;
      filterMenu.hidden = true;
      if (filterToggle) filterToggle.setAttribute('aria-expanded', 'false');
    }
    function toggleMenu() {
      if (!filterMenu) return;
      var open = filterMenu.hidden;
      filterMenu.hidden = !open;
      if (filterToggle) filterToggle.setAttribute('aria-expanded', String(open));
    }

    /* ----- mobile panel ----- */
    function openPanel() {
      if (!filterPanel) return;
      filterPanel.hidden = false;
      if (filterOpenBtn) filterOpenBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      filterPanel.focus();
    }
    function closePanel() {
      if (!filterPanel || filterPanel.hidden) return;
      filterPanel.hidden = true;
      document.documentElement.style.overflow = '';
      if (filterOpenBtn) {
        filterOpenBtn.setAttribute('aria-expanded', 'false');
        filterOpenBtn.focus();
      }
    }

    /* ----- events ----- */
    function onClick(event) {
      var topicLink = event.target.closest('[data-video-topic]');
      if (topicLink && section.contains(topicLink)) {
        event.preventDefault();
        setTopic(topicLink.getAttribute('data-video-topic'), true);
        closeMenu();
        closePanel();
        return;
      }
      if (event.target.closest('[data-video-filter-toggle]')) {
        toggleMenu();
        return;
      }
      if (event.target.closest('[data-video-filter-open]')) {
        openPanel();
        return;
      }
      if (
        event.target.closest('[data-video-filter-close]') ||
        event.target === filterPanel
      ) {
        closePanel();
        return;
      }
      // Click outside the desktop dropdown closes it.
      if (filterMenu && !filterMenu.hidden && !event.target.closest('[data-video-filter]')) {
        closeMenu();
      }
    }

    // Bind to `section` (not document) so these listeners are torn down with the
    // section DOM on shopify:section:load — avoids stacking duplicate handlers
    // that would otherwise run against detached nodes. Escape still reaches here
    // because focus lives inside the section while a menu/panel is open.
    section.addEventListener('click', onClick);
    section.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeMenu();
        closePanel();
      }
    });

    // popstate can't bubble, so it must live on window — expose this instance's
    // handler and register a single shared window listener (see bindPopstateOnce).
    root.kenraVideoApplyTopic = function (topic) {
      setTopic(topic, false);
    };
    bindPopstateOnce();

    if (sentinel && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var matchCount = items.filter(matches).length;
            if (state.limit < matchCount) {
              state.limit += batchSize;
              apply();
            }
          });
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(sentinel);
    }

    // A direct /blogs/video/tagged/<Tag> landing is server-filtered to that tag,
    // so only that subset is in the DOM and in-place topic switching can't work.
    // For JS users, redirect to the base list with ?topic=<tag> so the full grid
    // renders and filtering behaves as designed (no-JS users keep the server view).
    var taggedMatch = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
    if (taggedMatch) {
      var taggedTopic = decodeURIComponent(taggedMatch[1].replace(/\+/g, ' ')).toLowerCase();
      window.location.replace(basePath + '?topic=' + encodeURIComponent(taggedTopic));
      return;
    }

    // Honour a ?topic= deep link on first load, else default to All.
    var initialTopic = new URLSearchParams(window.location.search).get('topic');
    if (initialTopic && section.querySelector('[data-video-topic="' + initialTopic + '"]')) {
      setTopic(initialTopic, false);
    } else {
      apply();
    }
  }

  function init() {
    document.querySelectorAll('[data-video-list]').forEach(initVideoList);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-video-list]').forEach(initVideoList);
  });
})();
